import apiService from './api';
import { getWebSocketUrl } from '../config/environment';

export interface AppNotification {
  id: string;
  type: 'weighing_started' | 'weighing_completed' | 'weighing_rejected' | 'weekly_summary' | 'stock_low' | 'system_alert';
  title: string;
  message: string;
  data?: any;
  read: boolean;
  created_at: string;
  user_id?: string;
}

export interface NotificationFilters {
  unread_only?: boolean;
  type?: string;
  limit?: number;
  offset?: number;
}

class NotificationService {
  private ws: WebSocket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;
  private listeners: ((notification: AppNotification) => void)[] = [];

  /**
   * Initialise la connexion WebSocket pour les notifications en temps réel
   */
  connect(userId: string, token: string) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      return;
    }

    const wsUrl = `${getWebSocketUrl()}/ws/notifications/${userId}/?token=${token}`;
    
    try {
      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = () => {
        this.reconnectAttempts = 0;
      };

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'notification') {
            this.handleNewNotification(data.notification);
          }
        } catch (error) {
          console.error('Erreur lors du parsing du message WebSocket:', error);
        }
      };

      this.ws.onclose = () => {
        this.attemptReconnect(userId, token);
      };

      this.ws.onerror = (error) => {
        console.error('Erreur WebSocket:', error);
      };

    } catch (error) {
      console.error('Erreur lors de la connexion WebSocket:', error);
    }
  }

  /**
   * Tente de reconnecter le WebSocket
   */
  private attemptReconnect(userId: string, token: string) {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      setTimeout(() => {
        this.connect(userId, token);
      }, this.reconnectDelay * this.reconnectAttempts);
    }
  }

  /**
   * Ferme la connexion WebSocket
   */
  disconnect() {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }

  /**
   * Ajoute un listener pour les nouvelles notifications
   */
  addListener(callback: (notification: AppNotification) => void) {
    this.listeners.push(callback);
  }

  /**
   * Supprime un listener
   */
  removeListener(callback: (notification: AppNotification) => void) {
    this.listeners = this.listeners.filter(listener => listener !== callback);
  }

  /**
   * Gère une nouvelle notification reçue
   */
  private handleNewNotification(notification: AppNotification) {
    this.listeners.forEach(listener => listener(notification));
  }

  /**
   * Récupère les notifications de l'utilisateur
   */
  async getNotifications(filters: NotificationFilters = {}): Promise<AppNotification[] | { results: AppNotification[], count: number, next: string | null, previous: string | null }> {
    try {
      const params = new URLSearchParams();
      if (filters.unread_only) params.append('unread_only', 'true');
      if (filters.type) params.append('type', filters.type);
      if (filters.limit) params.append('limit', filters.limit.toString());
      if (filters.offset) params.append('offset', filters.offset.toString());

      const url = `/notifications/notifications/?${params.toString()}`;
      const response = await apiService.get(url);
      
      // Retourner la réponse complète pour supporter la pagination
      return response.data;
    } catch (error) {
      console.error('🔔 API: Erreur lors de la récupération des notifications:', error);
      throw error;
    }
  }

  /**
   * Marque une notification comme lue
   */
  async markAsRead(notificationId: string): Promise<void> {
    try {
      await apiService.patch(`/notifications/notifications/${notificationId}/mark_read/`);
    } catch (error) {
      console.error('Erreur lors du marquage de la notification comme lue:', error);
      throw error;
    }
  }

  /**
   * Marque toutes les notifications comme lues
   */
  async markAllAsRead(): Promise<void> {
    try {
      await apiService.post('/notifications/notifications/mark_all_read/');
    } catch (error) {
      console.error('Erreur lors du marquage de toutes les notifications comme lues:', error);
      throw error;
    }
  }

  /**
   * Supprime une notification
   */
  async deleteNotification(notificationId: string): Promise<void> {
    try {
      await apiService.delete(`/notifications/notifications/${notificationId}/`);
    } catch (error) {
      console.error('Erreur lors de la suppression de la notification:', error);
      throw error;
    }
  }

  /**
   * Récupère le nombre de notifications non lues
   */
  async getUnreadCount(): Promise<number> {
    try {
      const response = await apiService.get('/notifications/notifications/unread_count/');
      return response.data.count || 0;
    } catch (error) {
      console.error('🔔 API: Erreur lors de la récupération du nombre de notifications non lues:', error);
      return 0;
    }
  }

  /**
   * Crée une notification de test (pour le développement)
   */
  async createTestNotification(type: AppNotification['type'], title: string, message: string): Promise<AppNotification> {
    try {
      const response = await apiService.post('/notifications/notifications/test/', {
        type,
        title,
        message
      });
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la création de la notification de test:', error);
      throw error;
    }
  }
}

// Instance singleton du service de notifications
const notificationService = new NotificationService();
export default notificationService;
